import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Twitter, Facebook, Linkedin, Mail, Link2, Check, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

function WhatsAppIcon({ size = 13, strokeWidth = 1.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21l1.65-4.05A9 9 0 1 1 8.05 20.35L3 21z" />
      <path d="M9 10c.5 2 2 3.5 4 4l1.2-1.2a1 1 0 0 1 1-.25l2.3.75a1 1 0 0 1 .7 1v2a1 1 0 0 1-1 1A9 9 0 0 1 8 9a1 1 0 0 1 1-1h2a1 1 0 0 1 1 .7l.75 2.3a1 1 0 0 1-.25 1L9 10z" />
    </svg>
  );
}

export default function ProductShare({ product }) {
  const { lang } = useLang();
  const en = lang === "en";
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `${window.location.origin}/api/og/boutique/${product.id}${en ? "?lang=en" : ""}`;
  const priceStr = `${(product.price / 100).toFixed(0)} €`;
  const text = en
    ? `${product.name}${product.artist ? ` — ${product.artist}` : ""} · ${priceStr} · The Fil Shop, Canal'Art 2026`
    : `${product.name}${product.artist ? ` — ${product.artist}` : ""} · ${priceStr} · La Boutique du Fil, Canal'Art 2026`;

  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  const mailSubject = encodeURIComponent(en ? `Discover: ${product.name} · The Fil Shop` : `À découvrir : ${product.name} · La Boutique du Fil`);
  const mailBody = encodeURIComponent(`${text}\n\n${url}`);

  const links = [
    { key: "facebook", Icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { key: "twitter", Icon: Twitter, label: "Twitter / X", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { key: "linkedin", Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { key: "whatsapp", Icon: WhatsAppIcon, label: "WhatsApp", href: `https://api.whatsapp.com/send?text=${t}%20${u}` },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* silent */ }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full inline-flex items-center justify-center gap-2 border border-fg/15 px-6 py-2.5 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-fg hover:border-fg/40 transition-colors"
        data-testid={`share-toggle-${product.id}`}
        aria-expanded={open}
      >
        {open ? <X size={12} /> : <Share2 size={12} />} {en ? "Share" : "Partager"}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.65, 0, 0.35, 1] }}
            className="overflow-hidden"
            data-testid={`share-panel-${product.id}`}
          >
            <div className="flex items-center justify-center gap-1.5 pt-3">
              {links.map(({ key, Icon, label, href }) => (
                <a
                  key={key}
                  href={href}
                  onClick={(e) => { e.preventDefault(); window.open(href, "_blank", "noopener,noreferrer,width=640,height=520"); }}
                  aria-label={`${en ? "Share on" : "Partager sur"} ${label}`}
                  data-testid={`share-${key}-${product.id}`}
                  className="w-9 h-9 border border-fg/25 flex items-center justify-center text-fg/70 hover:text-bg hover:bg-cinabre hover:border-cinabre transition-colors"
                >
                  <Icon size={13} strokeWidth={1.4} />
                </a>
              ))}
              <a
                href={`mailto:?subject=${mailSubject}&body=${mailBody}`}
                aria-label={en ? "Share by email" : "Partager par email"}
                data-testid={`share-email-${product.id}`}
                className="w-9 h-9 border border-fg/25 flex items-center justify-center text-fg/70 hover:text-bg hover:bg-cinabre hover:border-cinabre transition-colors"
              >
                <Mail size={13} strokeWidth={1.4} />
              </a>
              <button
                type="button"
                onClick={onCopy}
                aria-label={en ? "Copy link" : "Copier le lien"}
                data-testid={`share-copy-${product.id}`}
                className="w-9 h-9 border border-fg/25 flex items-center justify-center text-fg/70 hover:text-bg hover:bg-fg hover:border-fg transition-colors"
              >
                {copied ? <Check size={13} strokeWidth={1.6} /> : <Link2 size={13} strokeWidth={1.4} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
